import {
  ChangeDetectorRef, Component, OnInit,
} from '@angular/core';
import {
  ActivatedRoute, Router,
} from '@angular/router';
import { NotificationService } from '../../../application/services/NotificationService';
import { HttpResponse } from '../../../application/types/HttpResponse';
import { Client, ClientListResult } from '../../../clients/types/Client';
import { ClientApiService } from '../../../clients/services/client-api/ClientApiService';
import { EventApiService } from '../../../events/services/event-api/EventApiService';
import { EventTicket, SportsEvent } from '../../../events/types/Event';
import { OrderApiService } from '../../services/order-api/OrderApiService';
import { Order, SaveOrderData, UpdateOrderData } from '../../types/Order';

interface TicketRowGroup {
  row: string;
  tickets: EventTicket[];
}

interface TicketSectorGroup {
  sector: string;
  ticketsCount: number;
  rows: TicketRowGroup[];
}

@Component({
  selector: 'app-order-form-page',
  standalone: false,
  templateUrl: './order-form-page.component.html',
  styleUrl: './order-form-page.component.sass',
})
export class OrderFormPageComponent implements OnInit {
  public clients: Client[] = [];
  public events: SportsEvent[] = [];
  public order: Order | null = null;
  public form: SaveOrderData = this.createEmptyForm();
  public priceDrafts: Record<string, number> = {};
  public clientSearch = '';
  public loading = false;
  public saving = false;
  public errorMessage: string | null = null;
  private readonly naturalCollator = new Intl.Collator('ru-RU', { numeric: true, sensitivity: 'base' });

  public constructor(
    private route: ActivatedRoute,
    private router: Router,
    private orderApi: OrderApiService,
    private clientApi: ClientApiService,
    private eventApi: EventApiService,
    private notificationService: NotificationService,
    private changeDetector: ChangeDetectorRef,
  ) { }

  public async ngOnInit(): Promise<void> {
    await this.loadData();
  }

  public get selectedEvent(): SportsEvent | null {
    return this.events.find((event) => event.id === this.form.eventId) ?? null;
  }

  public get isEditMode(): boolean {
    return Boolean(this.route.snapshot.paramMap.get('orderId'));
  }

  public get pageTitle(): string {
    return this.isEditMode ? `Редактирование ${this.order?.code ?? 'заказа'}` : 'Новый заказ';
  }

  public get availableTickets(): EventTicket[] {
    return [...(this.selectedEvent?.tickets.filter((ticket) => ticket.status === 'available' || this.isTicketSelected(ticket.id)) ?? [])]
      .sort((left, right) => this.compareTickets(left, right));
  }

  public get filteredClients(): Client[] {
    const search = this.normalizeSearch(this.clientSearch);
    const selectedClient = this.clients.find((client) => client.id === this.form.clientId) ?? null;
    const matchedClients = search
      ? this.clients.filter((client) => this.normalizeSearch(this.getClientSearchText(client)).includes(search))
      : this.clients;

    if (selectedClient && !matchedClients.some((client) => client.id === selectedClient.id)) {
      return [selectedClient, ...matchedClients];
    }

    return matchedClients;
  }

  public get ticketSectorGroups(): TicketSectorGroup[] {
    const sectors = new Map<string, Map<string, EventTicket[]>>();
    for (const ticket of this.availableTickets) {
      const sector = ticket.sector || 'Без сектора';
      const row = ticket.row || 'Без ряда';
      const rows = sectors.get(sector) ?? new Map<string, EventTicket[]>();
      const tickets = rows.get(row) ?? [];
      tickets.push(ticket);
      rows.set(row, tickets);
      sectors.set(sector, rows);
    }

    return [...sectors.entries()]
      .sort(([leftSector], [rightSector]) => this.naturalCollator.compare(leftSector, rightSector))
      .map(([sector, rows]) => {
        const rowGroups = [...rows.entries()]
          .sort(([leftRow], [rightRow]) => this.naturalCollator.compare(leftRow, rightRow))
          .map(([row, tickets]) => ({
            row,
            tickets: tickets.sort((left, right) => this.naturalCollator.compare(left.seat, right.seat)),
          }));
        return {
          sector,
          rows: rowGroups,
          ticketsCount: rowGroups.reduce((sum, row) => sum + row.tickets.length, 0),
        };
      });
  }

  public get selectedTotal(): number {
    return this.form.ticketIds.reduce((total, ticketId) => total + this.getTicketPrice(ticketId), 0);
  }

  public get canSave(): boolean {
    return !this.saving
      && (!this.isEditMode || this.order?.status === 'reserved')
      && Boolean(this.form.clientId)
      && Boolean(this.form.eventId)
      && this.form.ticketIds.length > 0
      && this.form.ticketIds.every((ticketId) => this.isValidPrice(this.getTicketPrice(ticketId)));
  }

  public onEventChange(): void {
    this.form.ticketIds = [];
    this.priceDrafts = {};
  }

  public toggleTicket(ticketId: string): void {
    if (this.form.ticketIds.includes(ticketId)) {
      this.form.ticketIds = this.form.ticketIds.filter((id) => id !== ticketId);
      return;
    }

    this.form.ticketIds = [...this.form.ticketIds, ticketId];
    if (this.priceDrafts[ticketId] === undefined) {
      this.priceDrafts[ticketId] = this.findEventTicket(ticketId)?.salePrice ?? 0;
    }
  }

  public isTicketSelected(ticketId: string): boolean {
    return this.form.ticketIds.includes(ticketId);
  }

  public async saveOrder(): Promise<void> {
    if (!this.canSave) {
      this.errorMessage = 'Выберите клиента, событие и хотя бы один свободный билет.';
      return;
    }

    this.saving = true;
    this.errorMessage = null;
    try {
      const response = this.isEditMode
        ? await this.orderApi.updateOrder(this.route.snapshot.paramMap.get('orderId') ?? '', this.createUpdateData())
        : await this.orderApi.createOrder(this.form);
      const order = this.unwrapResponse(response);
      if (!order) {
        return;
      }

      this.notificationService.notify('order', this.isEditMode ? 'Заказ обновлен' : 'Создан резерв билетов', `${order.code}: ${order.items.length} шт.`);
      await this.router.navigate(['/dashboard/orders', order.code]);
    } catch (error) {
      this.handleUnknownError(error);
    } finally {
      this.saving = false;
      this.changeDetector.detectChanges();
    }
  }

  public formatMoney(value: number): string {
    return new Intl.NumberFormat('ru-RU', {
      style: 'currency',
      currency: 'RUB',
      maximumFractionDigits: 0,
    }).format(value);
  }

  public formatDate(value: string): string {
    return new Intl.DateTimeFormat('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    }).format(new Date(value));
  }

  public formatEvent(event: SportsEvent): string {
    return `${event.homeTeam.name} - ${event.awayTeam.name}`;
  }

  public getTicketPrice(ticketId: string): number {
    return this.priceDrafts[ticketId] ?? this.findEventTicket(ticketId)?.salePrice ?? 0;
  }

  public getClientName(client: Client): string {
    return `${client.firstName ?? ''} ${client.lastName ?? ''}`.trim() || client.telegramUsername || client.telegramId || 'Клиент';
  }

  public getClientOptionLabel(client: Client): string {
    const details = [
      client.phone,
      client.telegramUsername ? `@${client.telegramUsername.replace(/^@/, '')}` : null,
      client.telegramId,
      client.email,
    ].filter(Boolean);
    return details.length > 0 ? `${this.getClientName(client)} · ${details.join(' · ')}` : this.getClientName(client);
  }

  public trackById(_index: number, item: { id: string }): string {
    return item.id;
  }

  public trackBySector(_index: number, group: TicketSectorGroup): string {
    return group.sector;
  }

  public trackByRow(_index: number, group: TicketRowGroup): string {
    return group.row;
  }

  private async loadData(): Promise<void> {
    this.loading = true;
    this.errorMessage = null;
    try {
      const orderId = this.route.snapshot.paramMap.get('orderId');
      await Promise.all([
        this.loadClients(),
        this.loadEvents(),
        orderId ? this.loadOrder(orderId) : Promise.resolve(),
      ]);
    } catch (error) {
      this.handleUnknownError(error);
    } finally {
      this.loading = false;
      this.changeDetector.detectChanges();
    }
  }

  private async loadClients(): Promise<void> {
    const response = await this.clientApi.getClients({
      search: '',
      status: 'active',
      page: 1,
      pageSize: 500,
    });
    const result = this.unwrapResponse(response);
    if (result) {
      this.clients = (result as ClientListResult).clients;
    }
  }

  private async loadEvents(): Promise<void> {
    const response = await this.eventApi.getEvents();
    const result = this.unwrapResponse(response);
    if (result) {
      this.events = (result as SportsEvent[]).filter((event) => this.isEditMode || event.tickets.some((ticket) => ticket.status === 'available'));
    }
  }

  private async loadOrder(orderId: string): Promise<void> {
    const response = await this.orderApi.getOrder(orderId);
    const order = this.unwrapResponse(response);
    if (!order) {
      this.errorMessage = 'Заказ не найден.';
      return;
    }

    this.order = order;
    this.form = {
      clientId: order.clientId,
      eventId: order.eventId,
      ticketIds: order.items.map((item) => item.ticketId),
      reservationMinutes: this.getRemainingReservationMinutes(order),
      receivedByUserId: order.receivedByUserId,
    };
    this.priceDrafts = order.items.reduce<Record<string, number>>((drafts, item) => {
      drafts[item.ticketId] = item.unitSalePrice;
      return drafts;
    }, {});
  }

  private unwrapResponse<ResponseType>(response: HttpResponse<ResponseType>): ResponseType | null {
    if (response.status === 'failed') {
      this.errorMessage = response.errorMessage;
      return null;
    }

    return response.response;
  }

  private createEmptyForm(): SaveOrderData {
    return {
      clientId: '',
      eventId: '',
      ticketIds: [],
      reservationMinutes: 60,
    };
  }

  private createUpdateData(): UpdateOrderData {
    return {
      clientId: this.form.clientId,
      eventId: this.form.eventId,
      reservationMinutes: this.form.reservationMinutes,
      receivedByUserId: this.form.receivedByUserId ?? null,
      ticketItems: this.form.ticketIds.map((ticketId) => ({
        ticketId,
        unitSalePrice: this.getTicketPrice(ticketId),
      })),
    };
  }

  private findEventTicket(ticketId: string): EventTicket | null {
    return this.selectedEvent?.tickets.find((ticket) => ticket.id === ticketId) ?? null;
  }

  private compareTickets(left: EventTicket, right: EventTicket): number {
    return this.naturalCollator.compare(left.sector, right.sector)
      || this.naturalCollator.compare(left.row, right.row)
      || this.naturalCollator.compare(left.seat, right.seat)
      || this.naturalCollator.compare(left.code, right.code);
  }

  private getClientSearchText(client: Client): string {
    return [
      this.getClientName(client),
      client.publicIdentifier,
      client.telegramUsername,
      client.telegramId,
      client.maxId,
      client.phone,
      client.email,
    ].filter(Boolean).join(' ');
  }

  private normalizeSearch(value: string): string {
    return value.trim().toLowerCase();
  }

  private getRemainingReservationMinutes(order: Order): number {
    if (!order.reservationExpiresAt) {
      return 60;
    }

    const minutes = Math.ceil((new Date(order.reservationExpiresAt).getTime() - Date.now()) / 60_000);
    return Math.min(Math.max(minutes, 1), 10080);
  }

  private isValidPrice(value: number): boolean {
    return Number.isFinite(value) && value >= 0;
  }

  private handleUnknownError(error: unknown): void {
    this.errorMessage = error instanceof Error ? error.message : 'Не удалось выполнить действие.';
  }
}
